import { Icon } from "@chakra-ui/icons";
import { Box, Card, CardBody, CardHeader, Flex, Heading, IconButton, Spinner, Text, VStack } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowRight } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  OrgPublicProfileDto,
  OrganizationEntryDto,
  ContractStateDto,
  useListContractsQuery,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../../common/errors/useApiError";
import { urls } from "../../../../urls";
import { ContractsTable } from "../../../contracts/view-all/ContractsTable";

interface ContractsSectionProps {
  organization?: OrgPublicProfileDto;
  organizationEntry?: OrganizationEntryDto;
}

export const ContractsSection = ({ organization, organizationEntry }: ContractsSectionProps) => {
  const orgData = organization || organizationEntry;
  const { t } = useTranslation();
  const authState = useLoggedInWithOrgContextState();
  const displayer = useApiError();

  const [filters, setFilters] = useState({
    skip: 0,
    limit: 10,
  });

  const navigate = useNavigate();
  const getCounterpartyIds = (): string[] => {
    const ids: string[] = [];
    if (orgData?.id) ids.push(orgData.id);
    organization?.linkedEntries?.forEach((entry) => ids.push(entry.id));
    return ids;
  };

  const counterpartyIds = getCounterpartyIds();

  const {
    data: contracts,
    isLoading,
    isFetching,
    error,
  } = useListContractsQuery(
    {
      orgId: authState.selectedOrg.id,
      counterparties: counterpartyIds.join(","),
      state: ContractStateDto.Published,
      limit: filters.limit,
      skip: filters.skip,
    },
    { skip: counterpartyIds.length === 0 }
  );

  if (error) return displayer.show(error);

  return (
    <Box width="full" height="full">
      <VStack spacing={6} align="flex-start" width="full">
        <Heading size="md" pt="6">
          {t("Contracts")}
        </Heading>
        <Card width="full">
          <CardHeader>
            <Flex justifyContent="space-between" alignItems="center">
              <Text fontWeight="semibold">
                {organization?.linkedEntries?.length
                  ? t("Contracts with") + ` ${orgData?.name} ` + t("and linked organizations")
                  : t("Contracts with") + ` ${orgData?.name}`}
              </Text>
              <IconButton
                aria-label="go to contracts"
                icon={<Icon as={FaArrowRight} />}
                variant="link"
                colorScheme="cyan"
                float="right"
                onClick={() => {
                  const counterpartiesParam = counterpartyIds.join(",");
                  navigate(`${urls.contracts.viewPublished}?counterparty=${counterpartiesParam}`);
                }}
              />
            </Flex>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" my={10}>
                <Spinner size="xl" />
              </Flex>
            ) : (
              <ContractsTable
                contracts={contracts?.contracts ?? []}
                isLoading={isLoading || isFetching}
                skip={filters.skip}
                limit={filters.limit}
                setSkip={(skip) => setFilters((prev) => ({ ...prev, skip }))}
              />
            )}
          </CardBody>
        </Card>
      </VStack>
    </Box>
  );
};
